import { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { withRouter } from 'react-router-dom';
import { ButtonWithIcon, DateInput, LoadingBar, Select } from '../../../components/Common';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import Routes from '../../../routes';
import toast from 'react-hot-toast';
import moment from 'moment';
import { AllOption, fetchReportExport, getValueOrDefault } from '../../../components/PowerBI/utils';
import LoadingButton from '@mui/lab/LoadingButton';

const CARBR3XML = ({ }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [loadingExport, setLoadingExport] = useState(false);
    const [ErrorObject, setErrorObject] = useState({});

    const minAllowedDate = moment().subtract(5, 'years').startOf('year').toDate();

    const [formData, setFormData] = useState({
        startDate: `01/01/${moment().year()}`,
        endDate: `${moment().format('MM/DD/YYYY')}`,
        region: 0,
    });

    const allOption = { property: "regions", value: -1, name: "All" };
    const noRegionOption = { property: "regions", value: 0, name: "No Associated Region/Division" };

    const setDefaultFormData = (data) => {
        setFormData(prev => ({
            ...prev,
            startDate: `01/01/${moment().year()}`,
            endDate: `${moment().format('MM/DD/YYYY')}`,
            region: 0,
        }));
        setData({
            ...data,
            filterRegions: [allOption, noRegionOption, ...data.regions],
        });
    };

    const fetchDropdown = async () => {
        try {
            const response = await fetch(Routes.PowerBI.CARBR3XMLDropdown());
            const data = await response.json();
            setDefaultFormData(data);
            setLoading(false);
        } catch (error) {
            toast.error('Could not fetch CARBR3 XML dropdown data!', { duration: 5000 });
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDropdown();
    }, []);

    const validateFilter = () => {
        const errorObject = {};

        if (!formData.startDate || formData.startDate === "") {
            errorObject.startDate = "Start Date is required";
        }

        if (!formData.endDate || formData.endDate === "") {
            errorObject.endDate = "End Date is required";
        }

        if (formData.startDate && formData.endDate) {
            const startDate = moment(formData.startDate, 'MM/DD/YYYY');
            const endDate = moment(formData.endDate, 'MM/DD/YYYY');
            const currentYear = moment().year();
            const currentDate = moment();
            const earliestAllowedDate = moment(`01/01/${currentYear - 5}`, 'MM/DD/YYYY');

            if (startDate.isBefore(earliestAllowedDate)) {
                errorObject.startDate = `Start date cannot be earlier than ${earliestAllowedDate.format('MM/DD/YYYY')}`;
            }

            if (endDate.isAfter(currentDate)) {
                errorObject.endDate = "End date cannot be later than today";
            }

            if (startDate.isAfter(endDate)) {
                errorObject.dateRange = "Start date must be before end date";
            }

            if (startDate.year() < currentYear && endDate.year() === currentYear) {
                errorObject.dateRange = "Prior year start and end dates must be in the same year";
            }
            if (startDate.year() < currentYear && endDate.year() < currentYear) {
                if (startDate.year() !== endDate.year()) {
                    errorObject.dateRange = "Prior year start and end dates must be in the same year";
                }
            }

            if (formData.region === '' || formData.region <= 0) {
                errorObject.region = `Region/Division is required`;
            }
            
        }

        const hasError = Object.keys(errorObject).length > 0;
        if (hasError) {
            const errorMessages = Object.values(errorObject).join(", ");
            toast.error(`${errorMessages}`, { duration: 4000 });
        }

        setErrorObject(errorObject);
        return hasError;
    };

    const exportReport = async (exportFormat) => {
        if (validateFilter()) {
            return;
        }

        console.log(formData);
        const requestData = {
            StartDate: moment(formData.startDate, 'MM/DD/YYYY').format('YYYY-MM-DDTHH:mm:ss'),
            EndDate: moment(formData.endDate, 'MM/DD/YYYY').format('YYYY-MM-DDTHH:mm:ss'),
            RegionId: formData.region.value,
            ExportFormat: exportFormat
        };
        setLoadingExport(true);
        await fetchReportExport({
            url: Routes.PowerBI.ExportR3XML(),
            body: requestData,
        });
        setLoadingExport(false);
    };

    const handleDateChange = (field) => (newDate) => {
        setFormData(prev => ({
            ...prev,
            [field]: newDate
        }));
        
        if (ErrorObject[field]) {
            setErrorObject(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                if (field === 'startDate' || field === 'endDate') {
                    delete newErrors.dateRange;
                }
                return newErrors;
            });
        }
    };
    if (loading) return <LoadingBar />;
    return (
        <>
            <Grid container spacing={2} sx={{ maxWidth: '65%' }}>
                <Grid item xs={12}>
                    {(loadingExport === false) ? (
                        <ButtonWithIcon
                            buttonText='Generate XML'
                            onClick={() => exportReport('Xml')}
                            iconPosition="start"
                            Icon={FileUploadOutlinedIcon}
                            disabled={loadingExport}
                        />
                    ) : (
                        <LoadingButton loading variant="contained">
                            <span>Generate XML</span>
                        </LoadingButton>
                    )}
                    <span style={{ marginLeft: '10px' }}></span>
                    {(loadingExport === false) ? (
                        <ButtonWithIcon
                            buttonText='Export (.XLSX)'
                            onClick={() => exportReport('Excel')}
                            iconPosition="start"
                            Icon={FileUploadOutlinedIcon}
                            disabled={loadingExport}
                        />
                    ) : (
                        <LoadingButton loading variant="contained">
                            <span>Generate XML</span>
                        </LoadingButton>
                    )}
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <DateInput
                        allowPast
                        variant="standard"
                        date={formData.startDate}
                        onChange={handleDateChange('startDate')}
                        label="Start Date *"
                        error={!!ErrorObject.startDate}
                        minDate={minAllowedDate}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <DateInput
                        allowPast
                        variant="standard"
                        date={formData.endDate}
                        onChange={handleDateChange('endDate')}
                        label="End Date *"
                        error={!!ErrorObject.endDate}
                        minDate={minAllowedDate}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Select
                        label='Region/Division *'
                        isFilterObject
                        variant="standard"
                        options={data.filterRegions}
                        onChange={e => {
                            setFormData(prev => ({
                                ...prev,
                                region: e.target.value
                            }));
                        }}
                    />
                </Grid>
            </Grid>

        </>
    );
};

export default withRouter(CARBR3XML);
